import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { QueueColumnHeaders } from './queue-column-headers';
import { useQueueStore } from '../context/use-queue-store';
import { Grid, Box } from '@cad-arc';
import { ColumnDefinitionEnumType } from '@mark43/cad-api';
import { QUEUE_GUTTER_WIDTH } from './queue-row-gutter';

export const QueueHeaderRow = observer(function QueueHeaderRow({
    children,
}: React.PropsWithChildren<Record<never, never>>): React.ReactElement {
    const store = useQueueStore();

    const handleResize = useCallback(
        (width: string, columnDefinitionName: ColumnDefinitionEnumType) => {
            store.workspacePanel.queue.setColumnWidth(columnDefinitionName, parseInt(width, 10));
        },
        [store]
    );
    const handleSort = useCallback(
        (definition: ColumnDefinitionEnumType) => {
            store.setSort(definition);
        },
        [store]
    );

    const handleReorder = useCallback(
        (columnDefinitionName: ColumnDefinitionEnumType, newIndex: number) => {
            store.workspacePanel.queue.setColumnOrder(columnDefinitionName, newIndex);
        },
        [store]
    );

    const { showsRowGutter, queueColumns } = store.workspacePanel.queue;
    const columnWidths = queueColumns.map(({ width }) => `${width}px`);

    return (
        <Box
            style={{
                '--columns': (showsRowGutter
                    ? [QUEUE_GUTTER_WIDTH, ...columnWidths]
                    : columnWidths
                ).join(' '),
            }}
        >
            <Grid
                templateColumns="var(--columns)"
                position="sticky"
                top="0px"
                zIndex="5"
                minHeight="22"
            >
                {/* The row gutter's column has no header and can't be moved or resized */}
                {showsRowGutter ? <Box backgroundColor="surface.accent" /> : null}
                {/* Separate component for column rendering so that we do not re-render each header during reordering or resizing because of new react elements for the headers  */}
                <QueueColumnHeaders
                    workspaceQueue={store.workspacePanel.queue}
                    onReorder={handleReorder}
                    onResize={handleResize}
                    onSort={handleSort}
                />
            </Grid>
            {children}
        </Box>
    );
});
